import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { PlayCircle, XCircle, ClockCountdown, Robot } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const ActivationControl = () => {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/activation-policies`, { withCredentials: true });
      const onlyImportedBases = (data || []).filter((lead) =>
        lead.intake_channel === 'upload' ||
        lead.intake_source === 'upload' ||
        lead.activation_mode === 'AUTO_24H'
      );
      setRows(onlyImportedBases);
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar ativações');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const selectedIds = Object.entries(selected)
    .filter(([, value]) => value)
    .map(([id]) => id);

  const toggle = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const startNow = async () => {
    setProcessing(true);
    try {
      const payload = selectedIds.length ? { lead_ids: selectedIds } : {};
      const { data } = await axios.post(`${API}/admin/activation-policies/start-now`, payload, { withCredentials: true });
      toast.success(`${data.activated || 0} leads liberados para IA`);
      setSelected({});
      fetchRows();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao iniciar ativações');
    } finally {
      setProcessing(false);
    }
  };

  const cancelSelected = async () => {
    if (!selectedIds.length) {
      toast.error('Selecione ao menos um lead para cancelar');
      return;
    }

    setProcessing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/activation-policies/cancel`,
        { lead_ids: selectedIds },
        { withCredentials: true }
      );
      toast.success(`${data.cancelled || 0} ativações canceladas`);
      setSelected({});
      fetchRows();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao cancelar ativações');
    } finally {
      setProcessing(false);
    }
  };

  const processDue = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/admin/activation-policies/process-due`, {}, { withCredentials: true });
      toast.success(`${data.activated || 0} ativações vencidas processadas`);
      fetchRows();
    } catch (e) {
      console.error(e);
      toast.error('Erro ao processar ativações vencidas');
    } finally {
      setProcessing(false);
    }
  };

  const waiting = rows.filter((r) => r.activation_status === 'awaiting_manager').length;
  const ready = rows.filter((r) => r.activation_status === 'ready').length;
  const scheduled = rows.filter((r) => r.activation_status === 'scheduled').length;

  const statusLabel = (status) => {
    if (status === 'ready') return 'Pronto para IA';
    if (status === 'awaiting_manager') return 'Bases aguardando';
    if (status === 'scheduled') return 'Agendado';
    return status || 'Não informado';
  };

  const badgeClass = (status) => {
    if (status === 'ready') return 'bg-emerald-50 text-emerald-700';
    if (status === 'awaiting_manager') return 'bg-amber-50 text-amber-700';
    if (status === 'scheduled') return 'bg-blue-50 text-blue-700';
    return 'bg-slate-50 text-slate-700';
  };

  const fmtDate = (value) => {
    if (!value) return '--';
    try {
      return new Date(value).toLocaleString('pt-BR');
    } catch {
      return '--';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Base Activation Center</h1>
            <p className="text-slate-600 mt-1">
              Após a importação da base, os leads válidos e verificados serão atendidos automaticamente após 24h, caso o gestor não antecipe ou cancele o processamento.
            </p>
          </div>

          <div></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <ClockCountdown size={28} className="text-amber-600 mb-3" />
            <p className="text-sm text-slate-500">Bases aguardando</p>
            <h3 className="text-3xl font-bold text-slate-900">{waiting}</h3>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <Robot size={28} className="text-emerald-600 mb-3" />
            <p className="text-sm text-slate-500">Prontos para atendimento</p>
            <h3 className="text-3xl font-bold text-slate-900">{ready}</h3>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <ClockCountdown size={28} className="text-blue-600 mb-3" />
            <p className="text-sm text-slate-500">Autoativação</p>
            <h3 className="text-3xl font-bold text-slate-900">{scheduled}</h3>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <PlayCircle size={28} className="text-indigo-600 mb-3" />
            <p className="text-sm text-slate-500">Selecionados</p>
            <h3 className="text-3xl font-bold text-slate-900">{selectedIds.length}</h3>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
          <div>
            <h2 className="font-bold text-slate-900">Ações da base</h2>
            <p className="text-sm text-slate-500">
              Ative o atendimento imediatamente ou cancele o processamento caso a base tenha sido enviada por engano.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              onClick={startNow}
              disabled={processing}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              Ativar atendimento agora
            </button>

            <button
              onClick={cancelSelected}
              disabled={processing}
              className="px-4 py-2 rounded-lg bg-red-50 text-red-700 font-semibold hover:bg-red-100 disabled:opacity-60"
            >
              Cancelar processamento
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-5 border-b border-slate-200">
            <h2 className="font-bold text-slate-900">Bases aguardando ativação</h2>
            <p className="text-sm text-slate-500">
              Somente leads de bases importadas aparecem aqui. Canais ativos seguem automaticamente para atendimento.
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center text-slate-500">Carregando...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-slate-500">Nenhuma base importada aguardando ativação.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((lead) => (
                <div key={lead.id} className="p-5 grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                  <div className="md:col-span-1">
                    <input
                      type="checkbox"
                      checked={!!selected[lead.id]}
                      onChange={() => toggle(lead.id)}
                      className="w-4 h-4"
                    />
                  </div>

                  <div className="md:col-span-4">
                    <p className="font-semibold text-slate-900">{lead.name || 'Lead sem nome'}</p>
                    <p className="text-xs text-slate-500">{lead.phone || lead.email || 'Sem contato exibido'}</p>
                  </div>

                  <div className="md:col-span-2 text-sm text-slate-600">
                    {lead.source || lead.intake_channel || 'Origem não informada'}
                  </div>

                  <div className="md:col-span-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${badgeClass(lead.activation_status)}`}>
                      {statusLabel(lead.activation_status)}
                    </span>
                  </div>

                  <div className="md:col-span-2 text-sm text-slate-500">
                    {lead.activation_mode || '--'}
                    <br />
                    <span className="text-xs">{fmtDate(lead.activation_at)}</span>
                  </div>

                  <div className="md:col-span-1 text-right text-xs text-slate-400">
                    IA
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ActivationControl;
